<template>
  <div class="filters-container">
    <div class="filters-section">
      <b-form-group v-if="isRootUser" label="Empresa" class="company-filter">
        <searchable-select
          v-model="localFilter.companyId"
          :options="companyOptions"
          :placeholder="'Todas as Empresas'"
          :search-placeholder="'Buscar empresa...'"
          :use-company-filter="true"
          @input="applyNow"
        />
      </b-form-group>

      <!-- Pesquisa Simples (apenas protocolo) para usuários comuns -->
      <b-form-group
        v-if="!hasAdvancedSearch"
        label="Pesquisar"
        class="search-filter"
      >
        <b-input-group>
          <b-input-group-prepend>
            <b-input-group-text>
              <i class="fas fa-search" aria-hidden="true"></i>
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            v-model="localFilter.text"
            type="search"
            placeholder="Digite o protocolo"
            @input="debounceFilter"
            @keyup.enter="applyNow"
            @search="applyNow"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <!-- Pesquisa Avançada para root e masters -->
      <b-form-group
        v-if="hasAdvancedSearch"
        label="Pesquisar"
        class="search-filter"
      >
        <b-input-group>
          <b-input-group-prepend>
            <b-input-group-text>
              <i class="fas fa-search" aria-hidden="true"></i>
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            v-model="localFilter.text"
            type="search"
            placeholder="Pesquisar por empresa, protocolo, denunciado, natureza ou relato"
            @input="debounceFilter"
            @keyup.enter="applyNow"
            @search="applyNow"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Status" class="status-filter">
        <b-form-select
          v-model="localFilter.status"
          class="custom-select"
          @change="applyNow"
        >
          <b-form-select-option :value="null">Todos</b-form-select-option>
          <b-form-select-option :value="complaintStatus.NEW"
            >Nova</b-form-select-option
          >
          <b-form-select-option :value="complaintStatus.PROCESSING"
            >Em processamento</b-form-select-option
          >
          <b-form-select-option :value="complaintStatus.CONCLUDED"
            >Concluída</b-form-select-option
          >
        </b-form-select>
      </b-form-group>

      <b-form-group label="Período" class="date-range-filter">
        <date-range-picker
          :start-date.sync="localFilter.startDate"
          :end-date.sync="localFilter.endDate"
          @change="applyNow"
        />
      </b-form-group>

      <b-form-group
        v-if="profile.role === 'company-root' || profile.role === 'root'"
        label="Comitê"
        class="status-filter"
      >
        <b-form-checkbox
          v-model="localFilter.onlyMyCommittee"
          @change="applyNow"
        >
          Mostrar apenas denúncias em que participo do comitê
        </b-form-checkbox>
      </b-form-group>

      <b-button
        variant="outline-secondary"
        class="clear-filters-btn"
        @click="clearFilters"
      >
        <i class="fas fa-eraser mr-1"></i> Limpar
      </b-button>
    </div>
  </div>
</template>

<script>
import SearchableSelect from "@/components/SearchableSelect.vue";
import DateRangePicker from "@/components/DateRangePicker.vue";

export default {
  name: "FiltersBar",
  components: { SearchableSelect, DateRangePicker },
  props: {
    filter: {
      type: Object,
      required: true,
    },
    complaintStatus: {
      type: Object,
      required: true,
    },
    profile: {
      type: Object,
      required: true,
    },
    hasAdvancedSearch: {
      type: Boolean,
      default: false,
    },
    companyOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localFilter: { ...this.filter },
      filterTimeout: null,
    };
  },
  computed: {
    isRootUser() {
      return this.profile.role === "root";
    },
  },
  watch: {
    filter: {
      deep: true,
      handler(newVal) {
        this.localFilter = { ...newVal };
      },
    },
  },
  beforeDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    emitUpdate() {
      this.$emit("update:filter", { ...this.localFilter });
    },
    applyNow() {
      this.emitUpdate();
      this.$emit("apply");
    },
    debounceFilter() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyNow();
      }, 300);
    },
    clearFilters() {
      this.localFilter = {
        text: "",
        status: null,
        onlyMyCommittee: false,
        companyId: null,
        startDate: null,
        endDate: null,
      };
      this.applyNow();
    },
  },
};
</script>

<style scoped>
.filters-container {
  margin-bottom: 2rem;
}

.filters-section {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: nowrap;
}

.search-filter {
  margin-bottom: 0 !important;
  flex: 1 1 460px;
  min-width: 320px;
}

.search-filter ::v-deep .form-control {
  font-size: 1rem;
  height: calc(1.5em + 1rem + 2px);
}

.status-filter {
  flex: 0 0 220px;
  min-width: 220px;
}

.company-filter {
  flex: 0 0 220px;
  min-width: 220px;
}

.date-range-filter {
  flex: 0 0 280px;
  min-width: 280px;
}

::v-deep .company-filter .ss-control {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  height: calc(1.5em + 1rem + 2px);
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.filters-section .form-group label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.5rem;
}

@media (max-width: 1200px) {
  .filters-section {
    flex-wrap: wrap;
  }

  .search-filter {
    flex: 1 1 100%;
  }
}

.clear-filters-btn {
  height: calc(1.5em + 1rem + 2px);
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  white-space: nowrap;
}
</style>
